import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "../lib/api";
import { CoachPlan } from "./PricingSettings";
import { ErrorState } from "./Loading";

/**
 * Which plans one athlete's family may see and switch to, after they've been added (27 Sep debug notes
 * item 12).
 *
 * The same choice the coach made on the add-athlete form, changeable later. Narrowing it declines a
 * request the family has waiting for a plan just removed, and the server tells the family why.
 */
export function OfferedPlansEditor({
  athlete,
}: {
  athlete: { client_id: string; name: string; plan_id: string | null; offered_plan_ids?: string[] };
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["billing", "plans"],
    queryFn: () => apiGet<{ plans: CoachPlan[] }>("/billing/plans"),
  });
  const choosable = (query.data?.plans ?? [])
    .filter((plan) => !plan.archived && plan.id !== athlete.plan_id)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const initial = athlete.offered_plan_ids ?? choosable.map((plan) => plan.id);
  const [checked, setChecked] = useState<string[] | null>(null);
  const current = checked ?? initial;

  const save = useMutation({
    mutationFn: (planIds: string[]) => apiPut(`/billing/athletes/${athlete.client_id}/offered-plans`, { planIds }),
    onSuccess: async () => {
      setChecked(null);
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  if (choosable.length === 0) return null;

  const visible = choosable.map((plan) => plan.id).filter((id) => current.includes(id));
  const unchanged = visible.join(",") === choosable.map((plan) => plan.id).filter((id) => initial.includes(id)).join(",");

  return (
    <div className="plan-form">
      <p className="form-label">Plans {athlete.name}&rsquo;s family can switch to</p>
      {save.isError && <ErrorState error={save.error} />}
      <div className="checkbox-list">
        {choosable.map((plan) => (
          <label key={plan.id} className="checkbox-label">
            <input
              type="checkbox"
              checked={current.includes(plan.id)}
              onChange={(e) =>
                setChecked(e.target.checked ? [...current, plan.id] : current.filter((id) => id !== plan.id))
              }
            />
            {plan.name}
            {plan.requiresApproval && <span className="badge">Needs your approval</span>}
          </label>
        ))}
      </div>
      <button type="button" disabled={save.isPending || unchanged} onClick={() => save.mutate(visible)}>
        {save.isPending ? "Updating…" : "Update plan options"}
      </button>
    </div>
  );
}
